import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import parse from 'html-react-parser';
import sanitizeHtml from 'sanitize-html';

import BCard from 'react-bootstrap/Card';
import Col from 'react-bootstrap/Col';
import Container from 'react-bootstrap/Container';
import Badge from 'react-bootstrap/Badge';
import Image from 'react-bootstrap/Image';
import Modal from 'react-bootstrap/Modal';
import moment from 'moment';
import Row from 'react-bootstrap/Row';

import Card from '../card/Card';
import ZohoForm from '../ZohoForm/ZohoForm';
import userDetailsRequest from '../../helpers/requests/userDetailsRequest';
import clip from '../../assets/clipBanner.png';
import clipSupport from '../../assets/clipSupport.png';
import { ReactComponent as Shape } from '../../assets/shape.svg';
import Table from '../table/Table';
import { AccountContext } from '../../views/accountsView/account/AccountContext';
import { UPDATE_TICKETS, UPDATE_TICKETDETAILS } from '../../views/accountsView/account/AccountReducer';
import LoadingPage from '../loading/LoadingPage';
import RefreshPage from '../refresh/RefreshPage';
import ModalWrapperWithOpenButton from '../ModalWrapperWithOpenButton/ModalWrapperWithOpenButton';
import UpgradeSubscriptionButton from '../upgradeSubscriptionButton/UpgradeSubscriptionButton';
import ExternalRequest from '../../helpers/requests/externalRequests';
import ProductRequest from '../../helpers/requests/productRequests';
import DOCS_URLS from '../../constants/documentation.constants';

import TableStyles from '../table/Table.module.scss';
import SupportInfoStyles from './SupportInfo.module.scss';

/**
 * Component that renders much of the Support view.
 *
 * @param {string} type - Indicate type of content to render, i.e: LITE, OPENTICKET, etc.
 * @param {string} productId - Product external key from tab position (index).
 */
const SupportInfo = ({ type, productId }) => {
  const { accountId } = useParams();
  const [accountContext, accountDispatch] = AccountContext();
  const [userInfo, setUserInfo] = useState({ email: undefined, name: undefined });
  const [loadingTickets, setLoadingTickets] = useState(false);
  const [ticketDetail, setTicketDetail] = useState({});
  const [showModalTicketDetail, setModalTicketDetail] = useState(false);
  const [loadingTicketDetail, setLoadingTicketDetail] = useState(false);
  const [errorTicketDetail, setErrorTicketDetail] = useState(false);

  const tickets = productId ? accountContext.tickets[productId] : [];
  const canRenderTicketDetail = !loadingTicketDetail && !errorTicketDetail;
  const canRenderTicketRefresh = !loadingTicketDetail && errorTicketDetail;
  const canRenderTicketLoading = loadingTicketDetail && !errorTicketDetail;

  const responseTicketsHandler = async (response) => {
    setLoadingTickets(false);
    accountDispatch({ type: UPDATE_TICKETS, data: { [productId]: response.data } });
  };

  const getTickets = () => {
    setLoadingTickets(true);
    ProductRequest.getTickets(
      responseTicketsHandler,
      () => {
        setLoadingTickets(false);
      },
      productId,
    );
  };

  const responseTicketDetailsHandler = (response) => {
    accountDispatch({ type: UPDATE_TICKETDETAILS, data: { [response.id]: response } });
    setTicketDetail(response);
    setLoadingTicketDetail(false);
  };

  if (type === 'OPENTICKET') {
    useEffect(() => {
      userDetailsRequest.get().then((res) => setUserInfo({
        email: res.data.email,
        name: `${res.data.first_name} ${res.data.last_name}`,
      }));
    }, []);
  }

  if (type === 'EXPLAINED') {
    useEffect(() => {
      if (!accountContext.ticketLoading) {
        getTickets();
      }
    }, [productId]);
  }

  const renderTicketDetail = (ticketId) => {
    const currentTicket = tickets?.find((ticket) => ticket.id === ticketId);
    setTicketDetail({ id: ticketId, ticketNumber: currentTicket?.ticketNumber });
    setErrorTicketDetail(false);
    if (!accountContext.ticketDetails[ticketId]) {
      setLoadingTicketDetail(true);
      ExternalRequest.getTicketDetail(
        responseTicketDetailsHandler,
        () => {
          setLoadingTicketDetail(false);
          setErrorTicketDetail(true);
          setTicketDetail({ id: ticketId, ticketNumber: currentTicket?.ticketNumber });
        },
        ticketId,
      );
    } else {
      setTicketDetail(accountContext.ticketDetails[ticketId]);
    }
    setModalTicketDetail(true);
  };

  const getFullName = (object) => {
    const firstName = object?.firstName ? object.firstName : '';
    const lastName = object?.lastName ? object.lastName : '';

    return `${firstName} ${lastName}`.trim();
  };

  const columns = [
    {
      Header: 'Ticket Number',
      accessor: 'ticketNumber',
    },
    {
      Header: 'Date',
      id: 'createdTime',
      accessor: 'createdTime',
      Cell(rows) {
        const { row } = rows;
        return (
          <span className={TableStyles.tdTextSpace}>
            {
             row.values.createdTime !== null && moment(row.values.createdTime).format('DD/MM/YYYY')
            }
          </span>
        );
      },
    },
    {
      Header: 'Subject',
      accessor: 'subject',
      Cell(rows) {
        const { row } = rows;
        return (<span className={TableStyles.tdTextLeft}>{row.values.subject}</span>);
      },
    },
    {
      Header: 'Contact',
      id: 'contact',
      accessor: 'contact',
      Cell(rows) {
        const { row } = rows;

        return (
          <span
            className={TableStyles.tdTextSpace}
          >
            {getFullName(row.values.contact)}
          </span>
        );
      },
    },
    {
      Header: 'Email',
      accessor: 'email',
      Cell(rows) {
        const { row } = rows;
        return (<span className={TableStyles.tdTextSpace}>{row.values.email}</span>);
      },
    },
    {
      Header: 'Status',
      accessor: 'status',
      Cell(rows) {
        const { row } = rows;

        return (<span className={TableStyles.tdTextSpace}>{row.values.status}</span>);
      },
    },
    {
      Header: 'Due Date',
      id: 'dueDate',
      accessor: 'dueDate',
      Cell(rows) {
        const { row } = rows;

        return (<span className={TableStyles.tdTextSpace}>{row.values.dueDate !== null && moment(row.values.dueDate).format('DD/MM/YYYY')}</span>);
      },
    },
    {
      Header: 'Closed Time',
      id: 'closedTime',
      accessor: 'closedTime',
      Cell(rows) {
        const { row } = rows;

        return (<span className={TableStyles.tdTextSpace}>{row.values.closedTime !== null && moment(row.values.closedTime).format('DD/MM/YYYY')}</span>);
      },
    },
    {
      Header: 'Actions',
      id: 'id',
      accessor: 'id',
      Cell(rows) {
        const { row } = rows;
        const ticketId = row.values.id;

        return (
          <button
            type="button"
            className={SupportInfoStyles.link}
            onClick={() => renderTicketDetail(ticketId)}
          >
            Details
          </button>
        );
      },
    },
  ];

  const LITE = (
    <Card name="Card" unique hasMainElement={false}>
      <div className={SupportInfoStyles.wrapper} id="list">
        <p className={SupportInfoStyles.textInfo}>
          Your
          <span className={SupportInfoStyles.txtSubscription}>
            {' '}
            lite subscription
            {' '}
          </span>
          is limited in functionality and support.
        </p>
        <p className={SupportInfoStyles.textInfo}>
          You can easily upgrade to enjoy the additional benefits of the paid
          subscriptions and get access to additional funtionality and our
          customer support services.
        </p>
        <p className={SupportInfoStyles.textInfo}>
          <UpgradeSubscriptionButton as="link" productExternalKey={productId} />
          {' '}
          to enjoy the additional benefits of the paid subscriptions and get
          access to our customer support services.
        </p>
        <br />
        <div className="d-flex justify-content-center">
          <UpgradeSubscriptionButton productExternalKey={productId} />
        </div>
      </div>
    </Card>
  );

  const EXPLAINED = (
    <>
      <Card name="Card" hasMainElement={false} columnsNumber={1}>
        <div className={SupportInfoStyles.wrapper}>
          <p className={SupportInfoStyles.textInfo}>
            Your subscription is entitled to edunext&apos;s customer support
            services. These include:
          </p>
          <br />
          <ul className={SupportInfoStyles.textInfo}>
            <li>Requests</li>
            <li>Inquiries</li>
            <li>Incidents</li>
            <li>Customer success consulting</li>
          </ul>
          <br />
          <p className={SupportInfoStyles.textInfo}>
            You will find all the details about these services in our
            {' '}
            <a
              href={DOCS_URLS.sla}
              target="_blank"
              rel="noopener noreferrer"
              className={SupportInfoStyles.link}
            >
              {' '}
              customer support agreement.
            </a>
          </p>
        </div>
        <div className={SupportInfoStyles.wrapper} id="list">
          <h3 className={SupportInfoStyles.titleCard}>
            Keep track of your open tickets
          </h3>
          <p className={SupportInfoStyles.textInfo}>
            All the responses from the support team will be sent to
            the email that created the ticket. Additionally, you
            can view the open and recently closed tickets by any
            member of your team in the table below.
          </p>
          <br />
          <Table columns={columns} data={tickets} dataName="tickets" loading={loadingTickets} />
          <Modal
            show={showModalTicketDetail}
            onHide={() => setModalTicketDetail(false)}
            aria-labelledby="contained-modal-title-vcenter"
            size="lg"
          >
            <Modal.Header closeButton closeVariant="white">
              <Modal.Title className="new" id="contained-modal-title-vcenter">
                {`Ticket Number ${ticketDetail.ticketNumber ? ticketDetail.ticketNumber : '...'}`}
              </Modal.Title>
            </Modal.Header>
            <Modal.Body className="show-grid">
              <Container>
                {
                  canRenderTicketLoading && (
                    <LoadingPage height="420px" message="Loading ticket information..." />
                  )
                }
                {
                  canRenderTicketDetail && (
                    <div className="p-3">
                      <div className="pb-5">
                        <h2>
                          {
                            ticketDetail.status && (
                              <small>
                                <Badge pill bg="primary">
                                  {ticketDetail.status}
                                </Badge>
                              </small>
                            )
                          }
                        </h2>
                        <h2>{ticketDetail.subject}</h2>
                      </div>
                      <Row xs={1} md={2} className="pb-4">
                        <Col>
                          <h4>Contact</h4>
                          <p>
                            <div>{getFullName(ticketDetail.contact)}</div>
                            <strong>{ticketDetail.contact?.email}</strong>
                            <div><small>{ticketDetail.contact?.account?.accountName}</small></div>
                          </p>
                        </Col>
                        <Col>
                          <h4>Agent</h4>
                          <div className="d-flex">
                            <Image
                              width="60px"
                              height="60px"
                              src={`data:image/png;base64,${ticketDetail.assignee?.photo}`}
                              roundedCircle
                            />
                            <p className="px-2">
                              <div>{getFullName(ticketDetail.assignee)}</div>
                              <strong>{ticketDetail.assignee?.email}</strong>
                            </p>
                          </div>
                        </Col>
                      </Row>
                      <div className="pb-5">
                        <h4>Description</h4>
                        { ticketDetail.description && (
                          <BCard>
                            <BCard.Body>
                              {parse(sanitizeHtml(ticketDetail.description))}
                            </BCard.Body>
                          </BCard>
                        )}
                      </div>
                      <div className="pb-5">
                        <h4>Last Thread</h4>
                        { ticketDetail.thread?.content && (
                          <BCard>
                            <BCard.Body>
                              {parse(sanitizeHtml(ticketDetail.thread?.content))}
                            </BCard.Body>
                          </BCard>
                        )}
                      </div>
                      <div className="pb-5">
                        <h4>Attachments</h4>
                        { ticketDetail.thread?.attachments && (
                          Object.entries(ticketDetail.thread.attachments).map(([, value]) => (
                            <div>
                              {
                                value.mime_type?.includes('image') ? (
                                  <Image
                                    width="100%"
                                    src={`data:${value.mime_type};base64,${value.file}`}
                                  />
                                ) : (
                                  <a
                                    rel="noreferrer"
                                    target="_blank"
                                    download={value.name}
                                    href={`data:${value.mime_type};base64,${value.file}`}
                                  >
                                    {value.name}
                                  </a>
                                )
                              }
                            </div>
                          ))
                        )}
                      </div>
                      <Row xs={1} md={3}>
                        <Col>
                          <h4>Date</h4>
                          <p>{ticketDetail.createdTime !== null && moment(ticketDetail.createdTime).format('DD/MM/YYYY')}</p>
                        </Col>
                        <Col>
                          <h4>Due Date</h4>
                          <p>{ticketDetail.dueDate !== null && moment(ticketDetail.dueDate).format('DD/MM/YYYY')}</p>
                        </Col>
                        <Col>
                          <h4>Close Date</h4>
                          <p>{ticketDetail.closedTime !== null && moment(ticketDetail.closedTime).format('DD/MM/YYYY')}</p>
                        </Col>
                      </Row>
                    </div>
                  )
                }
                {
                  canRenderTicketRefresh && (
                    <RefreshPage
                      height="420px"
                      onClick={() => renderTicketDetail(ticketDetail.id)}
                    />
                  )
                }
              </Container>
            </Modal.Body>
          </Modal>
        </div>
      </Card>
    </>
  );

  const OPENTICKET = (
    <Card name="Card" unique hasMainElement={false}>
      <div className={SupportInfoStyles.wrapper}>
        <h3 className={SupportInfoStyles.titleCard}>
          Open a new support ticket
        </h3>

        <p className={SupportInfoStyles.textInfo}>
          Use the button below to open a Support case for sending requests,
          raising inquiries, or reporting any incidents.
        </p>
        <div className={SupportInfoStyles.container}>
          <img
            src={clipSupport}
            alt="support"
            className={SupportInfoStyles.zohoIcon}
          />

          <div className={SupportInfoStyles.ticketContainer}>
            <p className={SupportInfoStyles.textInfoModal}>
              Cases will be reviewed, prioritized and resolved appropriately.
              <br />
              You’ll receive a response over email referencing the ticket
              number.
            </p>
            <ModalWrapperWithOpenButton
              title="Open a new support ticket"
              openButtonText="Open a new support ticket"
            >
              <ZohoForm
                user={{
                  name: userInfo.name,
                  email: userInfo.email,
                }}
                customProductId={productId}
                onFinishSubmit={getTickets}
                defaultType="Incident"
              />
            </ModalWrapperWithOpenButton>
          </div>
        </div>
        <div className={SupportInfoStyles.helpCard}>
          <h3 className={SupportInfoStyles.titleCard}>
            Help us provide the best level of support:
            {' '}
          </h3>
          <p className={SupportInfoStyles.textInfo}>
            <ul>
              <li>
                Include in your request all the necessary information, for
                example, the exact URL and a screenshot when necessary.
              </li>
              <li>
                Send any response or inquiry about an open case by replying to
                the corresponding email instead of opening a new support
                ticket.
              </li>
              <li>
                Open a new separated Case for each new request or inquiry you
                may have.
              </li>
            </ul>
          </p>
        </div>
      </div>
    </Card>
  );

  const EXPLORE_KNOWLEDGE = (
    <Card name="Card" unique hasMainElement={false}>
      <div className={SupportInfoStyles.wrapper}>
        <p className={SupportInfoStyles.textInfo}>
          Explore our
          {' '}
          <b>Documentation resources</b>
          {' '}
          to gain valuable information on
          how to manage your subscription, configure and manage your LMS site,
          create courses, launch and manage your
          courses, use analytics tools,
          and archive your Open edX courses.
        </p>
        <br />
        <div className="d-flex justify-content-center">
          <Link to={`/accounts/${accountId}/documentation/`} className={SupportInfoStyles.linkNormalButton}>
            edunext&apos;s Documentation
          </Link>
          <a
            href={DOCS_URLS.resources}
            target="_blank"
            className={SupportInfoStyles.linkNormalButton}
            rel="noopener noreferrer"
          >
            Resources
          </a>
        </div>
      </div>
    </Card>
  );

  const FOOTER = (
    <Card name="Card" columnsNumber={1} unique hasMainElement={false} className={SupportInfoStyles.cardContainer}>
      <div className={SupportInfoStyles.box}>
        <Shape className={SupportInfoStyles.shape} />
        <div className={SupportInfoStyles.footerWrapper}>
          <h1 className={SupportInfoStyles.footerTextInfo}>
            Grow strong in the Open edX world
            {' '}
            <br />
            with our qualified team of experts
          </h1>
          <img src={clip} alt="clip team" />
        </div>
      </div>
    </Card>
  );

  switch (type) {
    case 'LITE':
      return LITE;

    case 'EXPLAINED':
      return EXPLAINED;

    case 'OPENTICKET':
      return OPENTICKET;

    case 'EXPLORE_KNOWLEDGE':
      return EXPLORE_KNOWLEDGE;

    case 'FOOTER':
      return FOOTER;

    default:
      return <div />;
  }
};

SupportInfo.propTypes = {
  type: PropTypes.string.isRequired,
  productId: PropTypes.string,
};

SupportInfo.defaultProps = {
  productId: null,
};

export default SupportInfo;
